import React, { useState, useEffect, useRef } from "react";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&family=Roboto+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --primary:      #284ccf;
    --primary-dark: #102ab3;
    --success:      #10b981;
    --success-dark: #059669;
    --gold:         #f59e0b;
    --bg:           #f0f2fa;
    --white:        #ffffff;
    --gray-100:     #f1f3f9;
    --gray-200:     #e2e6ef;
    --gray-400:     #9ca3af;
    --gray-600:     #4b5563;
    --gray-900:     #111827;
    --radius-lg:    20px;
    --radius-xl:    28px;
    --shadow-soft:  0 8px 40px rgba(40,76,207,0.10);
    --shadow-card:  0 20px 60px rgba(0,0,0,0.08), 0 4px 16px rgba(40,76,207,0.06);
  }

  .ps-wrap * { font-family: 'Roboto', sans-serif !important; }

  html, body {
    font-family: 'Roboto', sans-serif !important;
    background: var(--bg);
    min-height: 100vh;
    color: var(--gray-900);
  }

  .ps-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 20px;
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #e8ecf8 0%, #f0f2fa 50%, #eef0f7 100%);
  }

  .ps-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 0;
    opacity: 0;
    transition: opacity 1s ease;
  }
  .ps-orb.visible { opacity: 1; }
  .ps-orb-1 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 70%);
    top: -100px; right: -100px;
    animation: orbFloat1 12s ease-in-out infinite;
  }
  .ps-orb-2 {
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(40,76,207,0.12) 0%, transparent 70%);
    bottom: -80px; left: -80px;
    animation: orbFloat2 15s ease-in-out infinite;
  }
  .ps-orb-3 {
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(245,158,11,0.10) 0%, transparent 70%);
    top: 40%; left: 50%;
    animation: orbFloat3 10s ease-in-out infinite;
  }
  @keyframes orbFloat1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-30px,40px)} }
  @keyframes orbFloat2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(40px,-30px)} }
  @keyframes orbFloat3 { 0%,100%{transform:translate(-50%,-50%)} 50%{transform:translate(-50%,-50%) translate(20px,-20px)} }

  #ps-confetti { position:fixed; inset:0; pointer-events:none; z-index:1; }

  .ps-card {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 520px;
    background: var(--white);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-card);
    border: 1px solid rgba(40,76,207,0.07);
    overflow: hidden;
    opacity: 0;
    transform: translateY(40px) scale(0.97);
    transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
  }
  .ps-card.visible { opacity: 1; transform: translateY(0) scale(1); }

  .ps-accent {
    height: 5px;
    background: linear-gradient(90deg, var(--primary), var(--primary-dark), var(--success), var(--gold));
    background-size: 200% 100%;
    animation: accentShift 4s linear infinite;
  }
  @keyframes accentShift { 0%{background-position:0% 0%} 100%{background-position:200% 0%} }

  .ps-body { padding: 44px 40px 40px; }
  @media(max-width:480px){ .ps-body { padding: 32px 24px 28px; } }

  .ps-icon-wrap {
    position: relative;
    width: 96px; height: 96px;
    margin: 0 auto 28px;
  }
  .ps-icon-ring {
    position: absolute; inset: 0;
    border-radius: 50%;
    border: 2.5px solid transparent;
    background: linear-gradient(var(--white), var(--white)) padding-box,
                linear-gradient(135deg, var(--success), var(--primary)) border-box;
    animation: ringPulse 2.5s ease-in-out infinite;
  }
  @keyframes ringPulse {
    0%,100% { transform: scale(1); opacity: 1; }
    50%      { transform: scale(1.08); opacity: 0.6; }
  }
  .ps-icon-ring-2 {
    position: absolute; inset: -8px;
    border-radius: 50%;
    border: 1.5px solid rgba(16,185,129,0.2);
    animation: ringPulse 2.5s ease-in-out 0.5s infinite;
  }
  .ps-icon-bg {
    position: absolute; inset: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(16,185,129,0.12), rgba(40,76,207,0.08));
    display: flex; align-items: center; justify-content: center;
  }
  .ps-icon-bg svg { width: 40px; height: 40px; }

  .ps-check-circle { stroke: url(#checkGrad); stroke-width: 2.5; fill: none; stroke-dasharray: 200; stroke-dashoffset: 200; }
  .ps-check-tick   { stroke: url(#checkGrad); stroke-width: 3; fill: none; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 40; stroke-dashoffset: 40; }
  .ps-icon-animate .ps-check-circle { animation: drawCircle 0.6s ease forwards 0.3s; }
  .ps-icon-animate .ps-check-tick   { animation: drawTick   0.4s ease forwards 1.0s; }
  @keyframes drawCircle { to { stroke-dashoffset: 0; } }
  @keyframes drawTick   { to { stroke-dashoffset: 0; } }

  .ps-title {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.5px;
    color: var(--gray-900);
    text-align: center;
    margin-bottom: 8px;
    opacity: 0;
    transform: translateY(12px);
    transition: opacity 0.5s ease 0.5s, transform 0.5s ease 0.5s;
  }
  .ps-title.visible { opacity: 1; transform: translateY(0); }

  .ps-subtitle {
    font-size: 14px;
    color: var(--gray-400);
    text-align: center;
    margin-bottom: 32px;
    line-height: 1.6;
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.5s ease 0.65s, transform 0.5s ease 0.65s;
  }
  .ps-subtitle.visible { opacity: 1; transform: translateY(0); }

  .ps-amount-pill {
    background: linear-gradient(135deg, rgba(16,185,129,0.08), rgba(40,76,207,0.05));
    border: 1px solid rgba(16,185,129,0.2);
    border-radius: 16px;
    padding: 20px 24px;
    text-align: center;
    margin-bottom: 28px;
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.5s ease 0.8s, transform 0.5s ease 0.8s;
  }
  .ps-amount-pill.visible { opacity: 1; transform: translateY(0); }
  .ps-amount-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.2px; color: var(--gray-400); margin-bottom: 6px; }
  .ps-amount-value {
    font-family: 'Roboto Mono', monospace !important;
    font-size: 52px !important;
    font-weight: 500 !important;
    background: linear-gradient(135deg, var(--success-dark), var(--primary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.1;
  }
  .ps-amount-currency {
  font-size: 24px;
  margin-left: 6px;
  opacity: 0.7;
  -webkit-text-fill-color: initial;
  color: var(--gray-600);
}

  .ps-details {
    border: 1px solid var(--gray-200);
    border-radius: var(--radius-lg);
    overflow: hidden;
    margin-bottom: 28px;
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.5s ease 0.95s, transform 0.5s ease 0.95s;
  }
  .ps-details.visible { opacity: 1; transform: translateY(0); }

  .ps-detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-bottom: 1px solid var(--gray-200);
    transition: background 0.2s;
  }
  .ps-detail-row:last-child { border-bottom: none; }
  .ps-detail-row:hover { background: var(--gray-100); }
  .ps-detail-key {
    font-size: 12.5px;
    color: var(--gray-400);
    font-weight: 500;
    display: flex; align-items: center; gap: 8px;
  }
  .ps-detail-key svg { width: 14px; height: 14px; opacity: 0.6; }
  .ps-detail-val {
    font-size: 13px;
    font-weight: 600;
    color: var(--gray-900);
    text-align: right;
    max-width: 60%;
    word-break: break-all;
  }
  .ps-detail-val.mono { font-family: 'Roboto Mono', monospace !important; font-size: 12px; }
  .ps-detail-val.badge-success {
    background: rgba(16,185,129,0.1);
    color: var(--success-dark);
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
  }

  .ps-copy-btn {
    background: none; border: none; cursor: pointer;
    color: var(--primary); padding: 2px 6px; border-radius: 6px;
    font-size: 11px; font-weight: 600; font-family: 'Roboto', sans-serif !important;
    transition: background 0.2s;
    display: inline-flex; align-items: center; gap: 4px;
  }
  .ps-copy-btn:hover { background: rgba(40,76,207,0.08); }

  .ps-footer-note {
    text-align: center;
    font-size: 11.5px;
    color: var(--gray-400);
    margin-top: 20px;
    line-height: 1.6;
    opacity: 0;
    transition: opacity 0.5s ease 1.4s;
  }
  .ps-footer-note.visible { opacity: 1; }
  .ps-footer-note a { color: var(--primary); text-decoration: none; font-weight: 500; }

  .ps-toast {
    position: fixed;
    bottom: 32px; left: 50%;
    transform: translateX(-50%) translateY(20px);
    background: var(--gray-900);
    color: white;
    font-size: 13px; font-weight: 500;
    padding: 10px 20px;
    border-radius: 30px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s, transform 0.25s;
    z-index: 100;
    white-space: nowrap;
  }
  .ps-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

// ─── Confetti ─────────────────────────────────────────────────────────────────
const launchConfetti = (canvas) => {
  const ctx = canvas.getContext("2d");
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const colors = [
    "#284ccf",
    "#10b981",
    "#f59e0b",
    "#7c3aed",
    "#ef4444",
    "#06b6d4",
  ];
  const particles = Array.from({ length: 110 }, () => ({
    x: Math.random() * canvas.width,
    y: -20,
    r: Math.random() * 6 + 3,
    d: Math.random() * 2 + 1,
    color: colors[Math.floor(Math.random() * colors.length)],
    tilt: Math.random() * 10 - 5,
    tiltAngle: 0,
    tiltSpeed: Math.random() * 0.07 + 0.05,
    type: Math.random() > 0.5 ? "circle" : "rect",
    rotation: Math.random() * Math.PI * 2,
    rotSpeed: (Math.random() - 0.5) * 0.08,
  }));

  let frame = 0;
  const animate = () => {
    if (frame > 220) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particles.forEach((p) => {
      p.y += p.d + (frame < 60 ? 3 : 1);
      p.tiltAngle += p.tiltSpeed;
      p.tilt = Math.sin(p.tiltAngle) * 12;
      p.rotation += p.rotSpeed;
      ctx.save();
      ctx.translate(p.x + p.tilt, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = frame > 160 ? 1 - (frame - 160) / 60 : 1;
      if (p.type === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, p.r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
      }
      ctx.restore();
    });
    frame++;
    requestAnimationFrame(animate);
  };
  animate();
};

// ─── Parse URL data param ─────────────────────────────────────────────────────
const parseUrlData = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get("data");
    if (!raw) return {};
    const parsed = JSON.parse(atob(raw));
    // Originals come as [{...}] arrays; redirect payload is a plain object
    return Array.isArray(parsed) ? parsed[0] : parsed;
  } catch {
    return {};
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ExternalWalletPaymentSuccess = () => {
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pageData, setPageData] = useState({});
  const [displayAmount, setDisplayAmount] = useState("—");

  useEffect(() => {
    const decoded = parseUrlData();
    setPageData(decoded);

    // ── Record customer details ──────────────────────────────────────────────
    const brokerId = localStorage.getItem(SITENAMEALIAS + "_brokerId") || "";
    fetch(
      `${WEBSERVICE}/payment/addInvoiceCustomerDetails`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invoice_id: decoded.invoice_id || "",
          broker_id: brokerId,
          email: decoded.support_email || "",
          payment_method: "EXTERNAL_WALLET",
          customer_name: decoded.organization_name || "",
          customer_phone: decoded.support_phone || "",
          customer_address_line_1: decoded.address_line_1 || "",
          customer_address_line_2: decoded.address_line_2 || "",
          city: decoded.city || "",
          state: decoded.state || "",
          zip: decoded.zip || "",
          country: decoded.country || "",
        }),
      }
    )
      .then((r) => r.json())
      .then((res) => {
        if (res.error !== "0") {
          console.warn("addInvoiceCustomerDetails:", res.message || "Failed");
        }
      })
      .catch((err) =>
        console.warn("addInvoiceCustomerDetails network error:", err)
      );

    // ── Fee calculation ──────────────────────────────────────────────────────
    const applyFee =
      decoded.is_processing_fee_applied == 1 ||
      decoded.is_processing_fee_applied === "1";
    const baseAmt = parseFloat(
      decoded.amount_to_be_paid ?? decoded.fiat_amount ?? 0
    );

    if (applyFee && decoded.merchant_id && decoded.currency_id && baseAmt) {
      fetch(
        `${WEBSERVICE}/payment/calculateTransactionFee`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            merchantId: String(decoded.merchant_id),
            currencyId: String(decoded.currency_id),
            amount: parseFloat(baseAmt.toFixed(6)),
          }),
        }
      )
        .then((r) => r.json())
        .then((fd) => {
          if (fd.error === "0")
            setDisplayAmount(parseFloat(fd.totalAmount).toFixed(6));
          else setDisplayAmount(baseAmt.toFixed(6));
        })
        .catch(() => setDisplayAmount(baseAmt.toFixed(6)));
    } else {
      setDisplayAmount(baseAmt ? baseAmt.toFixed(6) : "—");
    }

    const t1 = setTimeout(() => setVisible(true), 100);
    const t2 = setTimeout(() => {
      if (canvasRef.current) launchConfetti(canvasRef.current);
    }, 300);
    return () => [t1, t2].forEach(clearTimeout);
  }, []);

  // ─── Derive display values from URL data ──────────────────────────────────
  const currency = pageData.currency || pageData.currencyCode || "—";
  const txHash = pageData.txn_hash || "—";
  const invoiceId = pageData.invoice_id || "—";
  const timestamp = pageData.timestamp || new Date().toLocaleString();

  const handleCopy = () => {
    navigator.clipboard?.writeText(txHash).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <style>{STYLES}</style>
      <canvas ref={canvasRef} id="ps-confetti" />

      {/* Background orbs */}
      <div className={`ps-orb ps-orb-1 ${visible ? "visible" : ""}`} />
      <div className={`ps-orb ps-orb-2 ${visible ? "visible" : ""}`} />
      <div className={`ps-orb ps-orb-3 ${visible ? "visible" : ""}`} />

      <div className="ps-wrap ps-page">
        <div className={`ps-card ${visible ? "visible" : ""}`}>
          <div className="ps-accent" />
          <div className="ps-body">
            {/* ── Icon ── */}
            <div className="ps-icon-wrap">
              <div className="ps-icon-ring-2" />
              <div className="ps-icon-ring" />
              <div className="ps-icon-bg">
                <svg
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={visible ? "ps-icon-animate" : ""}
                >
                  <defs>
                    <linearGradient id="checkGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#284ccf" />
                    </linearGradient>
                  </defs>
                  <circle cx="24" cy="24" r="21" className="ps-check-circle" />
                  <polyline
                    points="14,25 21,32 34,17"
                    className="ps-check-tick"
                  />
                </svg>
              </div>
            </div>

            {/* ── Title ── */}
            <h1 className={`ps-title ${visible ? "visible" : ""}`}>
              Payment Successful!
            </h1>
            <p className={`ps-subtitle ${visible ? "visible" : ""}`}>
              Your transaction has been confirmed on the blockchain.
              <br />A receipt has been sent to your email.
            </p>

            {/* ── Amount ── */}
            <div className={`ps-amount-pill ${visible ? "visible" : ""}`}>
              <div className="ps-amount-label">Amount Paid</div>
              <div className="ps-amount-value">
                {displayAmount}
                <span className="ps-amount-currency">{currency}</span>
              </div>
            </div>

            {/* ── Details ── */}
            <div className={`ps-details ${visible ? "visible" : ""}`}>
              {[
                {
                  icon: (
                    <svg viewBox="0 0 16 16" fill="none">
                      <rect
                        x="1"
                        y="3"
                        width="14"
                        height="10"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M1 7h14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  ),
                  key: "Invoice ID",
                  val: invoiceId,
                  cls: "mono",
                },
                {
                  icon: (
                    <svg viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 2a6 6 0 100 12A6 6 0 008 2zm0 0V8l3 2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  ),
                  key: "Timestamp",
                  val: timestamp,
                  cls: "",
                },
                {
                  icon: (
                    <svg viewBox="0 0 16 16" fill="none">
                      <path
                        d="M2 9l4 4L14 3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                  key: "Status",
                  val: "Confirmed",
                  cls: "badge-success",
                },
                {
                  icon: (
                    <svg viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8h10M8 3l5 5-5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                  key: "TX Hash",
                  val: txHash,
                  cls: "mono",
                  copyable: true,
                },
              ].map((row, i) => (
                <div className="ps-detail-row" key={i}>
                  <span className="ps-detail-key">
                    {row.icon}
                    {row.key}
                  </span>
                  <span className={`ps-detail-val ${row.cls || ""}`}>
                    {row.val}
                    {row.copyable && (
                      <button className="ps-copy-btn" onClick={handleCopy}>
                        {copied ? "✓ Copied" : "Copy"}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {/* ── Footer ── */}
            <p className={`ps-footer-note ${visible ? "visible" : ""}`}>
              Need help? <a href="#">Contact support</a> · Powered by{" "}
              <a href="#">HashCash</a>
            </p>
          </div>
        </div>
      </div>

      {/* Copied toast */}
      <div className={`ps-toast ${copied ? "show" : ""}`}>
        ✓ Transaction hash copied
      </div>
    </>
  );
};

export default ExternalWalletPaymentSuccess;
